import type { OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import type { Image } from '@core-mkt/interfaces/image';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { TopSection } from '../../top-section/top-section/top-section';

interface CategoryWidgetContent {
  categoryTitle: string;
  categoryImage: Image[];
  categoryContent?: string;
}

// The @Input array element comes in two shapes (see the V2/V3 TODO in ngOnInit):
//  - V2: a wrapper carrying a nested `categoryWidgetContent` array.
//  - V3: the content object itself.
// Both content fields and the nested array are optional so either shape
// type-checks; the `Array.isArray` branch in ngOnInit selects the right one.
interface CategoryWidget extends Partial<CategoryWidgetContent> {
  categoryWidgetContent?: CategoryWidgetContent[];
}

@Component({
  selector: 'category-widget',
  templateUrl: './category-widget.component.html',
  styleUrls: ['./category-widget.component.scss'],
  standalone: true,
  imports: [LazyImgDirective],
})
export class CategoryWidgetComponent implements OnInit {
  @Input() categoryWidgets: CategoryWidget[];
  @Input() data: TopSection;
  theme: ThemeInterface;
  content: string;
  categoryWidgetContent?: CategoryWidgetContent;

  constructor(
    public imgixService: ImgixService,
    private themeParser: ThemeParserService,
    private redactor: WysiwygRedactorService,
  ) {}

  get title(): string | undefined {
    return this.categoryWidgetContent?.categoryTitle;
  }

  get image(): Image | undefined {
    return this.categoryWidgetContent?.categoryImage?.[0];
  }

  ngOnInit(): void {
    //TODO Remove this when V2 is deprecated
    if (this.categoryWidgets?.length) {
      if (Array.isArray(this.categoryWidgets[0].categoryWidgetContent)) {
        this.categoryWidgetContent = this.categoryWidgets[0].categoryWidgetContent[0];
      } else {
        // V3: the element itself is the content object (no nested array).
        this.categoryWidgetContent = this.categoryWidgets[0] as CategoryWidgetContent;
      }

      if (this.categoryWidgetContent?.categoryContent) {
        this.content = this.redactor.bypassSanitizer(this.categoryWidgetContent?.categoryContent);
      }
    }

    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }
}
